import { useMutation, useQueryClient, type QueryKey } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useOrganizationStore } from '@/core/stores/organization.store';
import { useToast } from '@/shared/hooks/useToast';
import { tasksService } from '../api/tasks.service';
import { TASK_KEYS } from './queryKeys';
import type { PlannedDate } from '@/shared/types/datetime';
import type { TaskSummaryDto } from '../types';

export interface ScheduleCommit {
  taskId: string;
  startDate: PlannedDate;
  dueDate: PlannedDate;
}

/**
 * Commits a drag/resize as a single PATCH on the moved task, optimistically.
 * Agnostic to which task moved. The backend owns cascading dependents, so on
 * settle we refetch the gantt rows to pick up any tasks it rescheduled.
 */
export function useGanttTaskSchedule(projectId: string) {
  const queryClient = useQueryClient();
  const orgId = useOrganizationStore((s) => s.activeOrganizationId)!;
  const { t } = useTranslation('tasks');
  const { toast } = useToast();

  // Matches every cached gantt-rows query for this project (key carries filters/search).
  const isGanttRowsKey = (key: QueryKey): boolean =>
    Array.isArray(key) && key[1] === orgId && key[2] === projectId && key[3] === 'gantt';

  return useMutation({
    mutationFn: ({ taskId, startDate, dueDate }: ScheduleCommit) =>
      tasksService.update(taskId, { startDate, dueDate }),

    onMutate: async ({ taskId, startDate, dueDate }) => {
      await queryClient.cancelQueries({ predicate: (q) => isGanttRowsKey(q.queryKey) });
      const snapshots = queryClient.getQueriesData<TaskSummaryDto[]>({
        predicate: (q) => isGanttRowsKey(q.queryKey),
      });
      for (const [key, data] of snapshots) {
        if (!data) continue;
        queryClient.setQueryData<TaskSummaryDto[]>(
          key,
          data.map((task) => (task.id === taskId ? { ...task, startDate, dueDate } : task)),
        );
      }
      return { snapshots };
    },

    onError: (_error, _vars, context) => {
      // Roll back the optimistic move; the global handler shows the error toast.
      context?.snapshots.forEach(([key, data]) => queryClient.setQueryData(key, data));
    },

    onSuccess: () => {
      toast.success({ title: t('gantt.schedule.updateSuccess') });
    },

    onSettled: () => {
      queryClient.invalidateQueries({ predicate: (q) => isGanttRowsKey(q.queryKey) });
      queryClient.invalidateQueries({ queryKey: TASK_KEYS.projectRoot(orgId, projectId) });
    },
  });
}
